import { AuthService } from '$services/auth/auth.service';
import { HorseService } from '$services/horse/horse.service';
import { IconsService } from '$services/icons/icons.service';
import { PostsService } from '$services/posts/posts.service';
import {
  ContactRole,
  CreatePostForm,
  HorseCategory,
  HorseDTO,
  HorseSex,
  PostDisplay,
  PostStatus,
  UpdatePostForm,
  User
} from '$types';
import { MaxSizeValidator } from '@angular-material-components/file-input';
import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';

interface DialogData {
  id?: string;
}

@Component({
  selector: 'app-create-update',
  templateUrl: './create-update.component.html',
  styleUrls: ['./create-update.component.css'],
})
export class CreateUpdateComponent implements OnInit {
  entityForm!: FormGroup;

  has_correct_sire: boolean = false;
  has_no_sire: boolean = false;
  files_max_size: number = 2000; // 2 Mo
  success_message: any;
  failure_message: any;
  horse: HorseDTO | null = null;
  user: User | null = null;
  post!: PostDisplay;

  constructor(
    public dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData | null,
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly horsesService: HorseService,
    private readonly translateService: TranslateService,
    private readonly snackBar: MatSnackBar,
    private readonly postService: PostsService,
    private readonly icons: IconsService,
  ) {
    this.icons.registerIcon(['file-import'])
  }

  ngOnInit(): void {
    this.user = this.authService.user;
    this.success_message = this.translateService.instant('success.save');
    this.failure_message = this.translateService.instant('errors.save');

    if (this.data?.id) {
      this.postService
        .findById(this.data.id)
        .subscribe((post) => {

          this.post = post

          this.entityForm = this.buildEntityForm();
          if (this.post.horse?.sire) {
            this.disableHorseFields();
            this.entityForm.get('sire')?.disable();
          }
        });
    } else {
      this.entityForm = this.buildEntityForm();
    }
  }

  get entityCreate(): CreatePostForm {
    return {
      idClient: this.user?.id || 'x0',
      category: this.category?.value.toUpperCase() || HorseCategory.YEARLING,
      sellPrice: this.sell_price?.value,
      commentsEn: this.comments_en?.value,
      commentsFr: this.comments_fr?.value,
      toNegociate: this.to_negociate?.value,
      taxableVat: this.taxable_vat?.value,
      mailConfirmation: this.mail_confirmation?.value,
      status: PostStatus.WAITING_VALIDATION,
      contact: {
        firstName: this.first_name?.value,
        lastName: this.last_name?.value,
        role: this.role?.value.toUpperCase() || ContactRole.UNIQUE_OWNER,
        landlinePhoneNumber: this.landline_phone_number?.value,
        mobilePhoneNumber: this.mobile_phone_number?.value,
        email: this.email?.value,
        isEnglish: this.contact_is_english?.value,
      },
      horse: {
        id: this.horse?.id || null,
        name: this.horse_name?.value,
        birthYear: this.horse_year?.value,
        sex: this.horse_sex?.value.toUpperCase() || HorseSex.MALE.toUpperCase(),
        raceRecord: this.horse_record?.value,
        totalEarn: this.horse_total_earn?.value,
        mother: this.horse_mother?.value,
        father: this.horse_father?.value,
        location: this.horse_location?.value,
        mailConfirmation: this.mail_confirmation?.value,
        sire: this.sire
      },
      photos: this.horse_photos?.value,
      certificateOfSale: this.documents_certificate_of_sale?.value,
      certificationOfThePerson: this.documents_certification_of_the_person?.value,
      extractKbis: this.documents_extract_kbis?.value,
      horseRegistration: this.documents_horse_registration?.value,
    };
  }

  get entityUpdate(): UpdatePostForm | null {
    if (!this.post) { return null };

    if ((!this.post.horse)) { return null }

    return {
      id: this.post.id,
      idClient: this.user?.id || 'x0',
      category: this.category?.value.toUpperCase() || HorseCategory.YEARLING,
      sellPrice: this.sell_price?.value,
      commentsEn: this.comments_en?.value,
      commentsFr: this.comments_fr?.value,
      toNegociate: this.to_negociate?.value,
      taxableVat: this.taxable_vat?.value,
      mailConfirmation: this.mail_confirmation?.value,
      status: PostStatus.WAITING_VALIDATION,
      contact: {
        firstName: this.first_name?.value,
        lastName: this.last_name?.value,
        role: this.role?.value.toUpperCase() || ContactRole.UNIQUE_OWNER,
        landlinePhoneNumber: this.landline_phone_number?.value,
        mobilePhoneNumber: this.mobile_phone_number?.value,
        email: this.email?.value,
        isEnglish: this.contact_is_english?.value,
      },
      horse: {
        id: this.post.horse.id,
        name: this.horse_name?.value,
        birthYear: this.horse_year?.value,
        sex: this.horse_sex?.value.toUpperCase() || HorseSex.MALE.toUpperCase(),
        raceRecord: this.horse_record?.value,
        totalEarn: this.horse_total_earn?.value,
        mother: this.horse_mother?.value,
        father: this.horse_father?.value,
        location: this.horse_location?.value,
        photos: this.horse_photos?.value,
        mailConfirmation: this.mail_confirmation?.value
      },
      photos: this.horse_photos?.value,
      certificateOfSale:
        this.documents_certificate_of_sale?.value ||
        this.post?.certificateOfSale,
      certificationOfThePerson:
        this.documents_certification_of_the_person?.value ||
        this.post?.certificationOfThePerson,
      extractKbis:
        this.documents_extract_kbis?.value ||
        this.post?.extractKbis,
      horseRegistration:
        this.documents_horse_registration?.value ||
        this.post?.horseRegistration
    };
  }

  get hasCorrectSire():boolean {
    return this.has_correct_sire;
  }
  get sire() {
    return this.entityForm.get('sire')?.value.trim();
  }

  get horse_name() {
    return this.entityForm.get('horse_informations')?.get('horse_name');
  }

  get horse_year() {
    return this.entityForm.get('horse_informations')?.get('horse_year');
  }

  get horse_sex() {
    return this.entityForm.get('horse_informations')?.get('horse_sex');
  }

  get horse_total_earn() {
    return this.entityForm.get('horse_informations')?.get('horse_total_earn');
  }

  get horse_record() {
    return this.entityForm.get('horse_informations')?.get('horse_record');
  }

  get horse_father() {
    return this.entityForm.get('horse_informations')?.get('horse_father');
  }

  get horse_mother() {
    return this.entityForm.get('horse_informations')?.get('horse_mother');
  }

  get category(): AbstractControl<any, any> | null | undefined {
    return this.entityForm.get('horse_informations')?.get('category');
  }

  get horse_location() {
    return this.entityForm.get('horse_informations')?.get('horse_location');
  }

  get horse_photos() {
    return this.entityForm.get('horse_informations')?.get('horse_photos');
  }

  get sell_price() {
    return this.entityForm.get('horse_informations')?.get('sell_price');
  }

  get to_negociate() {
    return this.entityForm.get('horse_informations')?.get('to_negociate');
  }

  get taxable_vat() {
    return this.entityForm.get('horse_informations')?.get('taxable_vat');
  }

  get comments_fr() {
    return this.entityForm.get('horse_informations')?.get('comments_fr');
  }

  get comments_en() {
    return this.entityForm.get('horse_informations')?.get('comments_en');
  }

  get last_name() {
    return this.entityForm.get('contact_informations')?.get('last_name');
  }

  get first_name() {
    return this.entityForm.get('contact_informations')?.get('first_name');
  }

  get role() {
    return this.entityForm.get('contact_informations')?.get('role');
  }

  get landline_phone_number() {
    return this.entityForm
      .get('contact_informations')
      ?.get('landline_phone_number');
  }

  get mobile_phone_number() {
    return this.entityForm
      .get('contact_informations')
      ?.get('mobile_phone_number');
  }

  get email() {
    return this.entityForm.get('contact_informations')?.get('email');
  }

  get documents_certificate_of_sale() {
    return this.entityForm.get('documents')?.get('certificate_of_sale');
  }

  get documents_certification_of_the_person() {
    return this.entityForm.get('documents')?.get('certification_of_the_person');
  }

  get documents_extract_kbis() {
    return this.entityForm.get('documents')?.get('extract_kbis');
  }

  get documents_horse_registration() {
    return this.entityForm.get('documents')?.get('horse_registration');
  }

  get contact_is_english() {
    return this.entityForm.get('contact_informations')?.get('is_english');
  }

  get mail_confirmation() {
    return this.entityForm.get('contact_informations')?.get('mail_confirmation');
  }

  getUploadErrors(
    errors: ValidationErrors | null | undefined
  ): ValidationErrors {
    if (!errors) return {};
    return errors;
  }

  buildEntityForm(): FormGroup<any> {
    const form: FormGroup = this.formBuilder.group({
      sire: [
        this.post?.horse?.sire || '',
        Validators.pattern(/^(\d+[A-Za-z])?$/),
      ],
      horse_informations: this.buildHorseForm(),
      contact_informations: this.buildContactForm(),
      documents: this.buildDocumentForm(),
    });

    form.get('sire')?.valueChanges.subscribe((sire: string) => {
      this.getHorseInfosFromSire(sire, form);
    });

    return form;
  }

  private getHorseInfosFromSire(sire: string, form: FormGroup<any>) {
    this.has_correct_sire = /^(\d+[A-Za-z])$/g.test(sire);
    if (this.has_correct_sire) {
      this.disableHorseFields();
      this.horsesService
        .findBySire(sire)
        .subscribe((horse) => this.setHorse(horse));
    } else {
      form.get('horse_informations')?.enable();
    }
  }

  buildDocumentForm() {
    return this.formBuilder.group({
      certificate_of_sale: [
        this.post?.certificateOfSale,
        MaxSizeValidator(this.files_max_size * 1024),
      ],
      certification_of_the_person: [
        this.post?.certificationOfThePerson,
        MaxSizeValidator(this.files_max_size * 1024),
      ],
      extract_kbis: [
        this.post?.extractKbis,
        MaxSizeValidator(this.files_max_size * 1024),
      ],
      horse_registration: [
        this.post?.horseRegistration,
        [Validators.required, MaxSizeValidator(this.files_max_size * 1024)],
      ],
    });
  }

  buildContactForm() {
    return this.formBuilder.group({
      last_name: [
        this.post?.contact?.lastName || '',
        Validators.required,
      ],
      first_name: [
        this.post?.contact?.firstName || '',
        Validators.required,
      ],
      role: [
        this.post?.contact?.role.toLowerCase() || '',
        Validators.required,
      ],
      landline_phone_number: [
        this.post?.contact?.landlinePhoneNumber || '',
      ],
      mobile_phone_number: [
        this.post?.contact?.mobilePhoneNumber || '',
        Validators.required,
      ],
      email: [
        this.post?.contact?.email || '',
        [Validators.required, Validators.email],
      ],
      is_english: [this.post?.contact?.isEnglish || false],
      mail_confirmation: [this.post?.mailConfirmation || false],
    });
  }

  buildHorseForm() {
    return this.formBuilder.group({
      horse_name: [
        this.post?.horse?.name || '', {
          required: true
        }],
      horse_year: [this.post?.horse?.birthYear || 2010, {
        required: true,
        disabled: this.hasCorrectSire
      }
      ],
      horse_sex: [this.post?.horse?.sex.toLowerCase() || 'female', {
        required: true,
        disabled: this.hasCorrectSire
      }
      ],
      horse_total_earn: [this.post?.horse?.totalEarn || 0, {
        required: true,
        disabled: this.hasCorrectSire
      }
      ],
      horse_record: [this.post?.horse?.raceRecord || '', {
        required: true,
        disabled: this.hasCorrectSire
      }
      ],
      horse_father: [this.post?.horse?.father || '', {
        required: true,
        disabled: this.hasCorrectSire
      }
      ],
      horse_mother: [this.post?.horse?.mother || '', {
        required: true,
        disabled: this.hasCorrectSire
      }
      ],
      category: [this.post?.category.toLowerCase() || '', {
        required: true
      }
      ],
      horse_location: [this.post?.horse?.location || '', {
        required: true
      }
      ],
      horse_photos: [this.post?.horse?.photos || [], {
        required: false
      }
      ],
      sell_price: [this.post?.sellPrice || 0, {
        required: true
      }],
      to_negociate: [this.post?.toNegociate || false],
      taxable_vat: [this.post?.taxableVat || false],
      comments_fr: [this.post?.commentsFr || ''],
      comments_en: [this.post?.commentsEn || ''],
    });
  }

  private setHorse(horse: HorseDTO | null) {
    if (horse === null) {
      this.horse_name?.setValue('');
      this.horse_sex?.setValue('');
      this.horse_year?.setValue(2010);
      this.horse_father?.setValue('');
      this.horse_mother?.setValue('');
      return;
    }
    this.horse = horse;
    this.horse_name?.setValue(horse.name);
    this.horse_sex?.setValue(horse.sex.toLowerCase());
    this.horse_year?.setValue(this.horse?.birthYear);
    this.horse_total_earn?.setValue(horse.totalEarn);
    this.horse_record?.setValue(horse.raceRecord);
    this.horse_father?.setValue(horse.father);
    this.horse_mother?.setValue(horse.mother);
  }

  private disableHorseFields() {
    this.horse_name?.disable();
    this.horse_sex?.disable();
    this.horse_year?.disable();
    this.horse_total_earn?.disable();
    this.horse_record?.disable();
    this.horse_father?.disable();
    this.horse_mother?.disable();
  }

  isUniqueOwner(): boolean {
    return this.post?.contact?.role === ContactRole.UNIQUE_OWNER;
  }

  isStableManager(): boolean {
    return this.post?.contact?.role === ContactRole.STABLE_MANAGER;
  }

  isAgent(): boolean {
    return this.post?.contact?.role === ContactRole.AGENT;
  }

  isCoowner(): boolean {
    return this.post?.contact?.role === ContactRole.COOWNER;
  }

  hasNoSire() {
    this.has_no_sire = true;
    this.setHorse(null);
  }

  save(): void {
    this.postService
      .create(this.entityCreate)
      .subscribe((post) => {
        const message = post ? this.success_message : this.failure_message,
          background_color = post ? 'bg-green' : 'bg-red'
        this.snackBar.open(message, 'Ok', {
          duration: 5000,
          verticalPosition: 'top',
          panelClass: [background_color],
        })
        this.dialogRef.close();
      });
  }

  create(): void {
    const success_message = this.translateService.instant('success.save');
    const failure_message = this.translateService.instant('errors.save');
    this.postService.create(this.entityCreate).subscribe((post) => {
      const message = post ? success_message : failure_message;
      const background_color = post ? 'bg-green' : 'bg-red';
      this.dialogRef.close();
      this.snackBar.open(message, 'Ok', {
        duration: 5000,
        verticalPosition: 'top',
        panelClass: [background_color],
      });
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
